import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import Layout from '../../../components/Layout';
import { toAssetUrl } from '../../../utils/assetBase';
import { confirm } from '../../../components/ConfirmDialog';

// Ev vitrini — public rezervasiya səhifəsində görünəcək kartoçkalar.
// Ev siyahısı Qızıl Farel API-dən gəlir; burada hər evə şəkil/video/qiymət/özəllik yazılır.

const emptyForm = {
    price: '', extra_person_price: '', breakfast_included: false, capacity_ideal: 2, capacity_max: 4,
    area_m2: '', rooms: 1, beds: 1, single_beds: 0, sofas: 0, description: '', amenities: '', active: true, sort_order: 0
};

const inputCls = 'w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none';
const labelCls = 'mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500';

// Sıxılmış sətirdə məlumat nişanı — ev açılmadan hər şey görünsün
const CHIP_TONE = {
    default: 'bg-gray-100 text-gray-600',
    muted:   'bg-gray-50 text-gray-400',
    amber:   'bg-amber-50 text-amber-700',
    dark:    'bg-gray-800 text-white',
};
const Chip = ({ children, tone = 'default' }) => (
    <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold ${CHIP_TONE[tone] || CHIP_TONE.default}`}>
        {children}
    </span>
);

const STATUS_BADGE = {
    yeni:   { label: 'Yeni',      cls: 'bg-blue-100 text-blue-700' },
    tesdiq: { label: 'Təsdiqləndi', cls: 'bg-emerald-100 text-emerald-700' },
    red:    { label: 'Rədd edildi', cls: 'bg-rose-100 text-rose-700' },
};

export default function HouseListing() {
    const [tab, setTab] = useState('houses');
    const [requests, setRequests] = useState([]);
    const [newCount, setNewCount] = useState(0);
    const [reqLoading, setReqLoading] = useState(false);
    const [houses, setHouses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openName, setOpenName] = useState(null);   // hansı ev açıqdır
    const [imgs, setImgs] = useState([]);             // açıq evin şəkilləri (lokal sıra)
    const [dragIdx, setDragIdx] = useState(null);
    const [overIdx, setOverIdx] = useState(null);   // üzərinə buraxılacaq hədəf
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(null);   // { pct, done, total, label }
    const [notifyNums, setNotifyNums] = useState('');
    const [childFreeAge, setChildFreeAge] = useState(10);
    const [notifySaving, setNotifySaving] = useState(false);
    const imgRef = useRef(null);
    const vidRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/house-listing');
            setHouses(res.data.data || []);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Evlər yüklənmədi.');
            setHouses([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadSettings = useCallback(async () => {
        try {
            const res = await api.get('/house-listing/settings');
            setNotifyNums(res.data.data?.booking_notify_numbers || '');
            setChildFreeAge(res.data.data?.booking_child_free_age ?? 10);
        } catch { /* ignore */ }
    }, []);

    const loadRequests = useCallback(async () => {
        setReqLoading(true);
        try {
            const res = await api.get('/house-listing/requests');
            setRequests(res.data.data || []);
            setNewCount(res.data.new_count || 0);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Sorğular yüklənmədi.');
        } finally {
            setReqLoading(false);
        }
    }, []);

    useEffect(() => { load(); loadSettings(); loadRequests(); }, [load, loadSettings, loadRequests]);

    const setReqStatus = async (id, status) => {
        try {
            await api.put(`/house-listing/requests/${id}`, { status });
            toast.success('Yeniləndi.');
            await loadRequests();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yenilənmədi.');
        }
    };

    const openHouse = (h) => {
        if (openName === h.group_key) { setOpenName(null); return; }
        setOpenName(h.group_key);
        const l = h.listing;
        setForm(l ? {
            price: l.price ?? '', extra_person_price: l.extra_person_price ?? '',
            breakfast_included: Boolean(Number(l.breakfast_included)),
            capacity_ideal: l.capacity_ideal ?? 2, capacity_max: l.capacity_max ?? 4,
            area_m2: l.area_m2 || '', rooms: l.rooms ?? 1, beds: l.beds ?? 1,
            single_beds: l.single_beds ?? 0, sofas: l.sofas ?? 0,
            description: l.description || '', amenities: l.amenities || '',
            active: Boolean(Number(l.active ?? 1)), sort_order: l.sort_order ?? 0
        } : { ...emptyForm });
    };

    const saveCard = async (h) => {
        setSaving(true);
        try {
            await api.put('/house-listing', { ...form, group_key: h.group_key });
            toast.success(`Yadda saxlanıldı — ${h.house_count} evə şamil olundu.`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yadda saxlanmadı.');
        } finally {
            setSaving(false);
        }
    };

    // Açıq evin şəkilləri serverdən yeniləndikcə lokal sıra da sinxron qalsın
    useEffect(() => {
        const h = houses.find(x => x.group_key === openName);
        setImgs(h?.listing?.images || []);
    }, [houses, openName]);

    // Şəkilləri BİR-BİR yüklə: faiz + "N/15 şəkil" dəqiq olsun və biri xəta versə
    // qalanları itməsin (hamısı tək sorğuda getsəydi, hansı şəkil olduğunu bilməzdik).
    const uploadImages = async (listingId, files) => {
        const list = Array.from(files || []);
        if (!list.length) return;
        setUploading(true);
        setProgress({ pct: 0, done: 0, total: list.length, label: 'şəkil' });
        let ok = 0;
        const failed = [];
        for (let i = 0; i < list.length; i++) {
            // try DÖVRƏNİN İÇİNDƏdir → problemli şəkil bütün yükləməni dayandırmır, keçilir
            try {
                const fd = new FormData();
                fd.append('images', list[i], list[i].name);
                await api.post(`/house-listing/${listingId}/images`, fd, {
                    timeout: 300000, // böyük şəkil + serverdə sıxılma → default 10s yetmir
                    onUploadProgress: (e) => {
                        const fp = e.total ? e.loaded / e.total : 0;
                        // 99%-də saxla: baytlar getdi, amma server hələ sıxır
                        setProgress({ pct: Math.min(99, Math.round(((i + fp) / list.length) * 100)), done: ok, total: list.length, label: 'şəkil' });
                    }
                });
                ok++;
            } catch (err) {
                failed.push(list[i].name);
                console.error('[house image skip]', list[i].name, err.response?.data?.msg || err.message);
            }
            setProgress({ pct: Math.round(((i + 1) / list.length) * 100), done: ok, total: list.length, label: 'şəkil' });
        }
        setUploading(false);
        setProgress(null);
        await load();
        if (imgRef.current) imgRef.current.value = '';

        if (failed.length && ok) {
            toast(`${ok} şəkil yükləndi · ${failed.length} keçildi: ${failed.slice(0, 3).join(', ')}${failed.length > 3 ? '…' : ''}`,
                { icon: '⚠️', duration: 6000 });
        } else if (failed.length) {
            toast.error(`Heç bir şəkil yüklənmədi (${failed.length} problemli).`);
        } else {
            toast.success(`${ok} şəkil əlavə edildi.`);
        }
    };

    const deleteAllImages = async (listingId, groupName, count) => {
        if (!(await confirm({
            title: 'Bütün şəkilləri sil',
            message: `"${groupName}" qrupunun ${count} şəkli tamamilə silinsin?\n\nBu geri qaytarıla bilməz.`,
            confirmText: 'Hamısını sil',
            tone: 'danger'
        }))) return;
        try {
            const res = await api.delete(`/house-listing/${listingId}/images`);
            toast.success(res.data?.msg || 'Şəkillər silindi.');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinmədi.');
        }
    };

    // Yeni sıranı serverə yaz (lokal sıra artıq dəyişib — optimistik)
    const persistOrder = async (listingId, list) => {
        try {
            await api.put(`/house-listing/${listingId}/images/order`, { ids: list.map(x => x.id) });
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Sıra yadda saxlanmadı.');
            await load(); // uğursuzsa serverin sırasına qayıt
        }
    };

    const onDrop = async (i, listingId) => {
        const src = dragIdx;
        setDragIdx(null);
        if (src === null || src === i) return;
        const next = [...imgs];
        const [moved] = next.splice(src, 1);
        next.splice(i, 0, moved);
        setImgs(next);
        await persistOrder(listingId, next);
    };

    const moveImg = async (i, dir, listingId) => {
        const j = i + dir;
        if (j < 0 || j >= imgs.length) return;
        const next = [...imgs];
        [next[i], next[j]] = [next[j], next[i]];
        setImgs(next);
        await persistOrder(listingId, next);
    };

    const deleteImage = async (imageId) => {
        if (!(await confirm({ message: 'Bu şəkil silinsin?', tone: 'danger', confirmText: 'Sil' }))) return;
        try {
            await api.delete(`/house-listing/images/${imageId}`);
            toast.success('Şəkil silindi.');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinmədi.');
        }
    };

    const uploadVideo = async (listingId, file) => {
        if (!file) return;
        setUploading(true);
        setProgress({ pct: 0, done: 0, total: 1, label: 'video' });
        try {
            const fd = new FormData();
            fd.append('video', file, file.name);
            await api.post(`/house-listing/${listingId}/video`, fd, {
                timeout: 600000, // video böyükdür
                onUploadProgress: (e) => {
                    const pct = e.total ? Math.min(99, Math.round((e.loaded / e.total) * 100)) : 0;
                    setProgress({ pct, done: 0, total: 1, label: 'video' });
                }
            });
            toast.success('Video yükləndi.');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Video yüklənmədi.');
        } finally {
            setUploading(false);
            setProgress(null);
            if (vidRef.current) vidRef.current.value = '';
        }
    };

    const deleteVideo = async (listingId) => {
        if (!(await confirm({ message: 'Video silinsin?', tone: 'danger', confirmText: 'Sil' }))) return;
        try {
            await api.delete(`/house-listing/${listingId}/video`);
            toast.success('Video silindi.');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinmədi.');
        }
    };

    const saveNotify = async () => {
        setNotifySaving(true);
        try {
            await api.put('/house-listing/settings', { booking_notify_numbers: notifyNums, booking_child_free_age: childFreeAge });
            toast.success('Yadda saxlanıldı.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yadda saxlanmadı.');
        } finally {
            setNotifySaving(false);
        }
    };

    return (
        <Layout>
            <div className="mb-5">
                <h1 className="text-xl font-black text-gray-800">🏡 Ev vitrini</h1>
                <p className="mt-0.5 text-sm text-gray-400">
                    Evlər tipinə görə qruplaşdırılıb (Eco - 1/2/3 → <b>Eco</b>). Bir dəfə yazırsan — qrupdakı bütün evlərə şamil olur.
                    Boşluq isə hər ev üçün ayrıca Qızıl Farel-dən hesablanır.
                </p>
            </div>

            {/* Tablar */}
            <div className="mb-5 flex gap-2">
                {[
                    { k: 'houses', label: '🏡 Evlər' },
                    { k: 'requests', label: `📩 Sorğular${newCount > 0 ? ` (${newCount})` : ''}` },
                ].map(t => (
                    <button key={t.k} onClick={() => setTab(t.k)}
                        className={`rounded-xl border px-4 py-2 text-sm font-semibold transition-all ${
                            tab === t.k ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200 bg-white text-gray-500 hover:border-blue-300'
                        }`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* ── SORĞULAR ── */}
            {tab === 'requests' && (
                reqLoading ? (
                    <div className="py-16 text-center text-gray-400">Yüklənir...</div>
                ) : requests.length === 0 ? (
                    <div className="rounded-xl border border-gray-200 bg-white py-16 text-center text-gray-400">
                        Hələ rezervasiya sorğusu yoxdur.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {requests.map((r) => {
                            const b = STATUS_BADGE[r.status] || STATUS_BADGE.yeni;
                            return (
                                <div key={r.id} className="rounded-xl border border-gray-200 bg-white p-4">
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-bold text-gray-800">{r.guest_name}</span>
                                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${b.cls}`}>{b.label}</span>
                                                <span className="text-[10px] text-gray-400">#{r.id}</span>
                                            </div>
                                            <div className="mt-1 text-xs text-gray-500">
                                                🏡 {r.house_name} · 📅 {String(r.check_in).slice(0, 10)} → {String(r.check_out).slice(0, 10)} ({r.nights} gecə)
                                            </div>
                                            <div className="mt-0.5 text-xs text-gray-500">
                                                📞 <a href={`tel:${r.phone}`} className="font-mono font-semibold text-blue-600">{r.phone}</a>
                                                {' · '}👤 {r.adults} nəfər
                                            </div>
                                            {r.note && <div className="mt-1 text-xs text-gray-400">📝 {r.note}</div>}
                                        </div>
                                        <div className="flex shrink-0 gap-1.5">
                                            {r.status !== 'tesdiq' && (
                                                <button onClick={() => setReqStatus(r.id, 'tesdiq')}
                                                    className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-100">
                                                    ✓ Təsdiqlə
                                                </button>
                                            )}
                                            {r.status !== 'red' && (
                                                <button onClick={() => setReqStatus(r.id, 'red')}
                                                    className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-700 hover:bg-rose-100">
                                                    ✕ Rədd et
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            )}

            {/* Rezervasiya bildiriş nömrələri */}
            {tab === 'houses' && (<>
            <div className="mb-5 rounded-xl border border-green-200 bg-green-50/40 p-4">
                <label className={labelCls}>📱 Rezervasiya sorğusu gedəcək WhatsApp nömrələri</label>
                <div className="flex flex-wrap gap-2">
                    <input
                        type="text"
                        value={notifyNums}
                        onChange={(e) => setNotifyNums(e.target.value)}
                        placeholder="0774111199, 0501234567"
                        className={`${inputCls} min-w-[220px] flex-1`}
                    />
                    <button onClick={saveNotify} disabled={notifySaving}
                        className="shrink-0 whitespace-nowrap rounded-xl bg-green-600 px-4 py-2 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50">
                        {notifySaving ? 'Saxlanılır...' : 'Saxla'}
                    </button>
                </div>
                <p className="mt-1.5 text-[11px] text-green-700/70">Vergüllə ayır. Müştəri rezervasiya sorğusu göndərəndə bu nömrələrə WhatsApp bildirişi gedəcək.</p>

                {/* Pulsuz uşaq yaş həddi */}
                <div className="mt-3 border-t border-green-200/60 pt-3">
                    <label className={labelCls}>👶 Pulsuz uşaq yaş həddi</label>
                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            type="number"
                            min={0}
                            max={17}
                            value={childFreeAge}
                            onChange={(e) => setChildFreeAge(e.target.value)}
                            className={`${inputCls} w-24`}
                        />
                        <span className="text-sm font-semibold text-gray-600">yaş və aşağı — pulsuz</span>
                        <button onClick={saveNotify} disabled={notifySaving}
                            className="shrink-0 whitespace-nowrap rounded-xl bg-green-600 px-4 py-2 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50">
                            {notifySaving ? 'Saxlanılır...' : 'Saxla'}
                        </button>
                    </div>
                    <p className="mt-1.5 text-[11px] text-green-700/70">
                        Bu yaşa qədər (daxil) uşaqlar pulsuzdur. Yuxarısı böyük kimi əlavə nəfər qiymətinə daxil olur.
                        Müştəri rezervasiyada hər uşağın yaşını seçir.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="py-16 text-center text-gray-400">Yüklənir...</div>
            ) : houses.length === 0 ? (
                <div className="rounded-xl border border-gray-200 bg-white py-16 text-center text-gray-400">
                    Ev tapılmadı. Qızıl Farel inteqrasiyası aktivdirmi?
                </div>
            ) : (
                <div className="space-y-3">
                    {houses.map((h) => {
                        const l = h.listing;
                        const isOpen = openName === h.group_key;
                        return (
                            <div key={h.group_key} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                                {/* Başlıq */}
                                <button type="button" onClick={() => openHouse(h)}
                                    className="flex w-full items-start justify-between gap-3 px-5 py-3 text-left transition-all hover:bg-gray-50">
                                    <div className="flex min-w-0 flex-1 items-start gap-3">
                                        {l?.images?.length > 0 ? (
                                            <img src={toAssetUrl(l.images[0].image_url)} alt=""
                                                className="h-16 w-20 shrink-0 rounded-lg border border-gray-200 object-cover" />
                                        ) : (
                                            <div className="flex h-16 w-20 shrink-0 items-center justify-center rounded-lg border border-dashed border-gray-200 text-2xl text-gray-300">🏡</div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-sm font-bold text-gray-800">{h.group_key}</span>
                                                <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700">
                                                    {h.house_count} ev
                                                </span>
                                                {l && Number(l.price) > 0 && (
                                                    <span className="rounded-full bg-primary-50 px-2 py-0.5 text-xs font-black text-primary-700">
                                                        {Number(l.price).toFixed(0)} ₼<span className="font-normal text-primary-500">/gecə</span>
                                                    </span>
                                                )}
                                                {l && Number(l.active) !== 1 && (
                                                    <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-bold text-gray-600">gizli</span>
                                                )}
                                            </div>

                                            {l ? (
                                                <div className="mt-1.5 flex flex-wrap gap-1">
                                                    {Number(l.area_m2) > 0 && <Chip>📐 {l.area_m2} m²</Chip>}
                                                    {Number(l.rooms) > 0 && <Chip>🚪 {l.rooms} otaq</Chip>}
                                                    <Chip>👤 {l.capacity_ideal}–{l.capacity_max} nəfər</Chip>
                                                    {Number(l.beds) > 0 && <Chip>🛏️ {l.beds} ikinəfərlik</Chip>}
                                                    {Number(l.single_beds) > 0 && <Chip>🛏️ {l.single_beds} tək</Chip>}
                                                    {Number(l.sofas) > 0 && <Chip>🛋️ {l.sofas} divan</Chip>}
                                                    {Number(l.breakfast_included) === 1 && <Chip tone="amber">🍳 səhər yeməyi</Chip>}
                                                    <Chip tone={l.images?.length ? 'default' : 'muted'}>🖼️ {l.images?.length || 0} şəkil</Chip>
                                                    {l.video_url && <Chip tone="dark">🎬 video</Chip>}
                                                    {l.amenities && <Chip>✓ {String(l.amenities).split('\n').filter(Boolean).length} özəllik</Chip>}
                                                </div>
                                            ) : (
                                                <div className="mt-1 text-[11px] font-semibold text-amber-600">kartoçka yoxdur — kliklə yarat</div>
                                            )}
                                            <div className="mt-1 text-[10px] text-gray-400">{h.houses.join(' · ')}</div>
                                        </div>
                                    </div>
                                    <span className="shrink-0 pt-1 text-gray-400">{isOpen ? '▲' : '▼'}</span>
                                </button>

                                {isOpen && (
                                    <div className="border-t border-gray-100 p-5">
                                        {/* Sahələr */}
                                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                            <div>
                                                <label className={labelCls}>Qiymət (₼/gecə, 2 nəfər)</label>
                                                <input type="number" min="0" step="0.01" value={form.price}
                                                    onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Əlavə nəfər (₼)</label>
                                                <input type="number" min="0" step="0.01" value={form.extra_person_price}
                                                    onChange={(e) => setForm({ ...form, extra_person_price: e.target.value })} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Sahə (m²)</label>
                                                <input type="number" min="0" value={form.area_m2}
                                                    onChange={(e) => setForm({ ...form, area_m2: e.target.value })} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>İdeal nəfər</label>
                                                <input type="number" min="1" value={form.capacity_ideal}
                                                    onChange={(e) => setForm({ ...form, capacity_ideal: e.target.value })} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Maks. nəfər</label>
                                                <input type="number" min="1" value={form.capacity_max}
                                                    onChange={(e) => setForm({ ...form, capacity_max: e.target.value })} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Otaq</label>
                                                <input type="number" min="0" value={form.rooms}
                                                    onChange={(e) => setForm({ ...form, rooms: e.target.value })} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>İkinəfərlik çarpayı</label>
                                                <input type="number" min="0" value={form.beds}
                                                    onChange={(e) => setForm({ ...form, beds: e.target.value })} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Tək nəfərlik çarpayı</label>
                                                <input type="number" min="0" value={form.single_beds}
                                                    onChange={(e) => setForm({ ...form, single_beds: e.target.value })} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Divan (açılan)</label>
                                                <input type="number" min="0" value={form.sofas}
                                                    onChange={(e) => setForm({ ...form, sofas: e.target.value })} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Sıra №</label>
                                                <input type="number" value={form.sort_order}
                                                    onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className={inputCls} />
                                            </div>
                                        </div>

                                        <div className="mt-3 flex flex-wrap gap-3">
                                            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/50 px-3 py-2 text-sm font-semibold text-amber-800">
                                                <input type="checkbox" checked={form.breakfast_included}
                                                    onChange={(e) => setForm({ ...form, breakfast_included: e.target.checked })}
                                                    className="h-4 w-4 rounded border-gray-300 text-amber-600" />
                                                🍳 Səhər yeməyi daxildir
                                            </label>
                                            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-600">
                                                <input type="checkbox" checked={form.active}
                                                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                                    className="h-4 w-4 rounded border-gray-300 text-primary-600" />
                                                Public səhifədə göstər
                                            </label>
                                        </div>

                                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                            <div>
                                                <label className={labelCls}>Təsvir</label>
                                                <textarea rows={4} value={form.description}
                                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                                    placeholder="Ev haqqında qısa məlumat..." className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Özəlliklər (hər sətir bir özəllik)</label>
                                                <textarea rows={4} value={form.amenities}
                                                    onChange={(e) => setForm({ ...form, amenities: e.target.value })}
                                                    placeholder={'Kondisioner\nWi-Fi\nMangal\nAvtomobil yeri'} className={inputCls} />
                                            </div>
                                        </div>

                                        <button onClick={() => saveCard(h)} disabled={saving}
                                            className="mt-4 w-full rounded-xl py-2.5 text-sm font-bold text-white transition-all disabled:opacity-50"
                                            style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}>
                                            {saving ? 'Saxlanılır...' : (l ? `💾 Yenilə — ${h.house_count} evə şamil et` : `➕ Kartoçka yarat — ${h.house_count} ev`)}
                                        </button>

                                        {/* Media — yalnız kartoçka yarandıqdan sonra */}
                                        {!l ? (
                                            <p className="mt-3 text-center text-[11px] text-gray-400">Şəkil/video əlavə etmək üçün əvvəlcə kartoçkanı yarat.</p>
                                        ) : (
                                            <div className="mt-5 border-t border-gray-100 pt-4">
                                                {/* Şəkillər — sürüşdürərək sırala */}
                                                <div className="mb-1 flex items-center justify-between gap-2">
                                                    <label className={`${labelCls} mb-0`}>Şəkillər {imgs.length > 0 && `(${imgs.length})`}</label>
                                                    {imgs.length > 0 && (
                                                        <button type="button" onClick={() => deleteAllImages(l.id, h.group_key, imgs.length)}
                                                            className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-[11px] font-bold text-red-600 transition-all hover:bg-red-100">
                                                            🗑️ Hamısını sil
                                                        </button>
                                                    )}
                                                </div>
                                                {imgs.length > 0 && (
                                                    <p className="mb-1.5 text-[11px] text-gray-400">
                                                        Sıranı <b>◀ ▶</b> düymələri ilə dəyiş (kompüterdə şəkli tutub sürüşdürə də bilərsən). Birinci şəkil üz qabığıdır.
                                                    </p>
                                                )}
                                                <div className="mb-2 flex flex-wrap gap-2">
                                                    {imgs.map((im, i) => (
                                                        <div key={im.id}
                                                            draggable
                                                            onDragStart={(e) => {
                                                                setDragIdx(i);
                                                                e.dataTransfer.effectAllowed = 'move';
                                                                // Firefox drag-i başlatmaq üçün data tələb edir
                                                                try { e.dataTransfer.setData('text/plain', String(im.id)); } catch { /* ignore */ }
                                                                // Təmiz kölgə: barmaq/kursor altında mərkəzləşmiş kart
                                                                try {
                                                                    const el = e.currentTarget;
                                                                    e.dataTransfer.setDragImage(el, el.offsetWidth / 2, el.offsetHeight / 2);
                                                                } catch { /* ignore */ }
                                                            }}
                                                            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overIdx !== i) setOverIdx(i); }}
                                                            onDragLeave={() => setOverIdx((v) => (v === i ? null : v))}
                                                            onDrop={() => { setOverIdx(null); onDrop(i, l.id); }}
                                                            onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                                                            className={`group relative cursor-grab rounded-lg active:cursor-grabbing ${dragIdx === i ? 'opacity-30' : ''} ${overIdx === i && dragIdx !== i ? 'ring-2 ring-primary-500 ring-offset-1' : ''}`}
                                                        >
                                                            {/* draggable=false → brauzerin öz şəkil sürükləməsi bizimkiylə toqquşmasın
                                                                (o, əyilmiş/qəribə kölgə yaradırdı) */}
                                                            <img src={toAssetUrl(im.image_url)} alt="" draggable={false}
                                                                className={`h-20 w-28 select-none rounded-lg border-2 object-cover ${i === 0 ? 'border-primary-400' : 'border-gray-200'}`} />
                                                            {i === 0 && (
                                                                <span className="absolute left-1 top-1 rounded bg-primary-600 px-1.5 py-0.5 text-[9px] font-bold text-white">ÜZ QABIĞI</span>
                                                            )}
                                                            {/* Mobildə HƏMİŞƏ görünür (toxunmada hover yoxdur), desktopda hover ilə */}
                                                            <button type="button" onClick={() => deleteImage(im.id)}
                                                                className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white opacity-100 transition-all sm:h-5 sm:w-5 sm:opacity-0 sm:group-hover:opacity-100">
                                                                ✕
                                                            </button>
                                                            {/* Sıralama oxları — toxunma ekranda drag işləmir, ona görə burada həmişə görünür */}
                                                            <div className="absolute inset-x-0 bottom-1 flex justify-center gap-1.5 opacity-100 transition-all sm:opacity-0 sm:group-hover:opacity-100">
                                                                <button type="button" disabled={i === 0} onClick={() => moveImg(i, -1, l.id)}
                                                                    className="flex h-6 w-6 items-center justify-center rounded bg-black/70 text-[11px] text-white disabled:opacity-30 sm:h-5 sm:w-5 sm:text-[10px]">◀</button>
                                                                <button type="button" disabled={i === imgs.length - 1} onClick={() => moveImg(i, 1, l.id)}
                                                                    className="flex h-6 w-6 items-center justify-center rounded bg-black/70 text-[11px] text-white disabled:opacity-30 sm:h-5 sm:w-5 sm:text-[10px]">▶</button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {imgs.length === 0 && (
                                                        <span className="text-[11px] text-gray-400">Şəkil yoxdur</span>
                                                    )}
                                                </div>
                                                <input ref={imgRef} type="file" accept="image/*" multiple disabled={uploading}
                                                    onChange={(e) => uploadImages(l.id, e.target.files)}
                                                    className="mb-4 block w-full text-xs text-gray-500 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-xs file:font-bold file:text-primary-700" />
                                                <p className="-mt-3 mb-4 text-[11px] text-gray-400">Bir dəfəyə maks. 20 şəkil (Ctrl/Shift ilə çoxlu seçim). Ölçü limiti yoxdur — böyük şəkillər avtomatik sıxılır, yükləmə bir neçə saniyə çəkə bilər.</p>

                                                {/* Video */}
                                                <label className={labelCls}>Video (maks. 50 MB — MP4/WEBM/MOV)</label>
                                                {l.video_url ? (
                                                    <div className="mb-2">
                                                        <video src={toAssetUrl(l.video_url)} controls
                                                            className="h-40 w-full max-w-md rounded-lg border border-gray-200 bg-black object-contain" />
                                                        <button type="button" onClick={() => deleteVideo(l.id)}
                                                            className="mt-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-100">
                                                            🗑️ Videonu sil
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="mb-2 block text-[11px] text-gray-400">Video yoxdur</span>
                                                )}
                                                <input ref={vidRef} type="file" accept="video/mp4,video/webm,video/quicktime" disabled={uploading}
                                                    onChange={(e) => uploadVideo(l.id, e.target.files?.[0])}
                                                    className="block w-full text-xs text-gray-500 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-xs file:font-bold file:text-primary-700" />
                                                {/* Yükləmə faizi — 45% · 7/15 şəkil */}
                                                {progress && (
                                                    <div className="mt-3 rounded-xl border border-primary-100 bg-primary-50/50 p-3">
                                                        <div className="mb-1.5 flex items-center justify-between text-[11px] font-bold text-primary-700">
                                                            <span>
                                                                {progress.pct}%
                                                                {progress.label === 'şəkil'
                                                                    ? ` · ${progress.done}/${progress.total} şəkil`
                                                                    : ' · video yüklənir'}
                                                            </span>
                                                            <span className="font-normal text-primary-500">
                                                                {progress.pct >= 99 && progress.done < progress.total ? 'sıxılır...' : 'yüklənir...'}
                                                            </span>
                                                        </div>
                                                        <div className="h-2 w-full overflow-hidden rounded-full bg-primary-100">
                                                            <div className="h-full rounded-full bg-primary-600 transition-all duration-200"
                                                                style={{ width: `${progress.pct}%` }} />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            </>)}
        </Layout>
    );
}
